import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiRequest,
  buildReportThemeSaveBody,
  getBootstrapData,
  ReportThemeEditorModal,
  type ReportThemeApiRow,
  type ReportThemeColors,
  type ReportThemeEditorApi,
  type ReportThemeEditorMode,
  type ReportThemeFontAssetRow,
  type ReportThemeFontSlot,
  type ReportThemeGoogleFontFamily,
} from '@tpct/admin-ui';
import { __ } from '@/hub-shared/utils/i18n';
import { throwIfReportFontUploadFailed } from '@/hub-shared/utils/reportFontUploadError';
import MediaUploader from '../../../companies/src/shared/MediaUploader';
import { AddItemIcon } from '../../../companies/src/shared/AddItemIcon';
import type { CompanyPageData } from '../types/company-page';

type ThemeApiRow = ReportThemeApiRow;

const getVsmeApiConfig = () => {
  const bs = getBootstrapData('AccountingHubPortal') as { restUrlVsme?: string; nonce: string };
  const base = (bs.restUrlVsme ?? '').replace(/\/?$/, '/');
  return { base, nonce: bs.nonce };
};

const vsmeRequest = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const { base, nonce } = getVsmeApiConfig();
  const res = await fetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(err.message ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
};

const uploadFontFile = async (companyId: number, file: File) => {
  const { base, nonce } = getVsmeApiConfig();
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`${base}companies/${companyId}/report-fonts`, {
    method: 'POST',
    headers: { 'X-WP-Nonce': nonce },
    credentials: 'same-origin',
    body,
  });
  if (!res.ok) {
    await throwIfReportFontUploadFailed(res, __);
  }
  const json = await res.json() as { data: { font: ReportThemeFontAssetRow } };
  return json.data.font;
};

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StarDefaultIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type ThemesTabProps = {
  companyId: number;
  data: CompanyPageData;
  queryKey: unknown[];
  bootstrapKey: string;
};

const ThemesTab = ({ companyId, data, queryKey, bootstrapKey }: ThemesTabProps) => {
  const queryClient = useQueryClient();
  const [themes, setThemes] = useState<ThemeApiRow[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [modal, setModal] = useState<ReportThemeEditorMode | null>(null);
  const [logoSaveState, setLogoSaveState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [logoError, setLogoError] = useState('');
  const [presentationSaveState, setPresentationSaveState] = useState<'idle' | 'saving' | 'error'>(
    'idle',
  );
  const [presentationError, setPresentationError] = useState('');
  const [settingDefaultThemeId, setSettingDefaultThemeId] = useState<number | null>(null);

  const themeEditorApi = useMemo<ReportThemeEditorApi>(
    () => ({
      listCompanyFonts: async (cid) => {
        const res = await vsmeRequest<{ data: { fonts: ReportThemeFontAssetRow[] } }>(
          'GET',
          `companies/${cid}/report-fonts`,
        ).catch(() => ({ data: { fonts: [] as ReportThemeFontAssetRow[] } }));
        return res.data?.fonts ?? [];
      },
      listGoogleFamilies: async () => {
        const res = await vsmeRequest<{ data: { families: ReportThemeGoogleFontFamily[] } }>(
          'GET',
          'report-fonts/google-families',
        ).catch(() => ({ data: { families: [] as ReportThemeGoogleFontFamily[] } }));
        return res.data?.families ?? [];
      },
      uploadCompanyFont: uploadFontFile,
    }),
    [],
  );

  const fetchThemes = useCallback(async () => {
    setLoadState('loading');
    try {
      const res = await vsmeRequest<{ success: boolean; data: { themes: ThemeApiRow[] } }>(
        'GET',
        `companies/${companyId}/report-themes`,
      );
      setThemes(res.data?.themes ?? []);
      setLoadState('ready');
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : __('Chargement impossible.'));
      setLoadState('error');
    }
  }, [companyId]);

  useEffect(() => {
    void fetchThemes();
  }, [fetchThemes]);

  const handleLogoChange = async (id: number | undefined) => {
    setLogoSaveState('saving');
    setLogoError('');
    try {
      const updatedData = await apiRequest<CompanyPageData>(
        `company/${companyId}`,
        { method: 'PATCH', body: JSON.stringify({ ...data, thumbnail_id: id ?? null }) },
        bootstrapKey,
      );
      if (updatedData) {
        queryClient.setQueryData(queryKey, updatedData);
      }
      void queryClient.invalidateQueries({ queryKey });
      setLogoSaveState('idle');
    } catch (e) {
      setLogoSaveState('error');
      setLogoError(e instanceof Error ? e.message : __('Erreur lors de la sauvegarde.'));
    }
  };

  const handlePresentationImageChange = async (id: number | undefined) => {
    setPresentationSaveState('saving');
    setPresentationError('');
    try {
      const updatedData = await apiRequest<CompanyPageData>(
        `company/${companyId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ ...data, presentation_image_id: id ?? null }),
        },
        bootstrapKey,
      );
      if (updatedData) {
        queryClient.setQueryData(queryKey, updatedData);
      }
      void queryClient.invalidateQueries({ queryKey });
      setPresentationSaveState('idle');
    } catch (e) {
      setPresentationSaveState('error');
      setPresentationError(e instanceof Error ? e.message : __('Erreur lors de la sauvegarde.'));
    }
  };

  const mediaBusy = logoSaveState === 'saving' || presentationSaveState === 'saving';

  const handleDelete = async (themeId: number) => {
    if (!window.confirm(__('Supprimer ce thème ?'))) return;
    try {
      await vsmeRequest('DELETE', `report-themes/${themeId}`);
      void fetchThemes();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : __('Erreur lors de la suppression.'));
    }
  };

  const handleSetDefault = async (themeId: number) => {
    setSettingDefaultThemeId(themeId);
    try {
      await vsmeRequest('POST', `companies/${companyId}/report-themes/${themeId}/default`);
      void fetchThemes();
    } catch (e) {
      window.alert(
        e instanceof Error ? e.message : __('Impossible de définir le thème par défaut.'),
      );
    } finally {
      setSettingDefaultThemeId(null);
    }
  };

  const handleModalSubmit = async (
    name: string,
    colors: ReportThemeColors,
    titleFont: ReportThemeFontSlot,
    bodyFont: ReportThemeFontSlot,
  ) => {
    if (modal === null) return;
    const body = buildReportThemeSaveBody(name, colors, titleFont, bodyFont);
    if (modal.kind === 'create') {
      await vsmeRequest('POST', `companies/${companyId}/report-themes`, body);
    } else {
      await vsmeRequest('PUT', `report-themes/${modal.theme.id}`, body);
    }
    setModal(null);
    void fetchThemes();
  };

  return (
    <>
      <div className="themes-tab">
        <section
          className="container-style themes-tab__section"
          aria-labelledby="themes-tab-logo-presentation-heading"
        >
          <h4 id="themes-tab-logo-presentation-heading">{__('Logo et présentation')}</h4>
          <div className="themes-tab__logo-row">
            <div className="themes-tab__logo-column">
              <MediaUploader
                value={data.thumbnail_id ?? undefined}
                media={data.thumbnail_media}
                onChange={(id) => { void handleLogoChange(id); }}
                label={__('Logo')}
                description={__("Le logo sera affiché dans l'en-tête des rapports.")}
                disabled={mediaBusy}
                bootstrapKey={bootstrapKey}
              />
            </div>
            <div className="themes-tab__logo-column">
              <MediaUploader
                value={data.presentation_image_id ?? undefined}
                media={data.presentation_media}
                onChange={(id) => { void handlePresentationImageChange(id); }}
                label={__('Image de présentation')}
                description={__('Image haute qualité utilisée dans le corps du rapport.')}
                disabled={mediaBusy}
                bootstrapKey={bootstrapKey}
              />
            </div>
          </div>
          {logoSaveState === 'error' && <p className="themes-tab__error">{logoError}</p>}
          {presentationSaveState === 'error' && (
            <p className="themes-tab__error">{presentationError}</p>
          )}
        </section>

        <section className="container-style themes-tab__section" aria-labelledby="themes-tab-themes-heading">
          <h4 id="themes-tab-themes-heading">{__('Thèmes')}</h4>
          {loadState === 'loading' && <p className="themes-tab__hint">{__('Chargement…')}</p>}
          {loadState === 'error' && <p className="themes-tab__error">{loadError}</p>}
          {loadState === 'ready' && (
            <div className="themes-tab__themes-grid">
              {themes.map((theme) => {
                const rowActionsBusy = settingDefaultThemeId === theme.id;
                return (
                  <div key={theme.id} className="container-style themes-tab__theme-card">
                    <div className="themes-tab__theme-card-actions">
                      {theme.is_default && (
                        <span className="themes-tab__theme-default-badge">{__('Défaut')}</span>
                      )}
                      {!theme.is_default && (
                        <button
                          type="button"
                          className="btn-icon themes-tab__action-btn"
                          disabled={rowActionsBusy}
                          onClick={() => { void handleSetDefault(theme.id); }}
                          aria-label={__('Définir comme thème par défaut')}
                        >
                          <StarDefaultIcon />
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-icon themes-tab__action-btn"
                        disabled={rowActionsBusy}
                        onClick={() => setModal({ kind: 'edit', theme })}
                        aria-label={__('Modifier le thème')}
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="btn-icon themes-tab__action-btn themes-tab__action-btn--delete"
                        disabled={rowActionsBusy}
                        onClick={() => { void handleDelete(theme.id); }}
                        aria-label={__('Supprimer le thème')}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                    <span
                      className="themes-tab__color-dot"
                      style={{ backgroundColor: theme.colors.main_dark }}
                      aria-hidden="true"
                    />
                    <p className="themes-tab__theme-label">{theme.name}</p>
                  </div>
                );
              })}
              <button
                type="button"
                className="container-style themes-tab__theme-card themes-tab__add-card"
                onClick={() => setModal({ kind: 'create' })}
                aria-label={__('Créer un thème')}
              >
                <AddItemIcon className="themes-tab__add-icon" />
              </button>
            </div>
          )}
        </section>
      </div>

      {modal !== null && (
        <ReportThemeEditorModal
          mode={modal}
          companyId={companyId}
          api={themeEditorApi}
          t={__}
          onClose={() => setModal(null)}
          onSubmit={handleModalSubmit}
        />
      )}
    </>
  );
};

export default ThemesTab;
